import { useRef, useState } from 'react'
import type { WireArticleBlock } from '../../../api/types'
import { useArticleArtifacts } from '../common/ArticleArtifactsContext'
import { useVisualEngagementContext } from '../common/VisualEngagementContext'
import { emitVisualInteraction, useVisualInViewTracking } from '../common/visualCollectionTracking'
import { PortionMark } from '../common/PortionMark'

type Block = Extract<WireArticleBlock, { type: 'photo-comparison' }>

const WIDTH_CLASS: Record<NonNullable<Block['width']>, string> = {
  column: 'mx-auto max-w-3xl',
  wide:   'mx-auto max-w-4xl',
  full:   'w-full',
}

export function PhotoComparisonBlock({ block }: { block: Block }) {
  const artifacts = useArticleArtifacts()
  const engagement = useVisualEngagementContext()
  const figureRef = useRef<HTMLElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState(block.initialPosition ?? 0.5)
  const [dragging, setDragging] = useState(false)
  const visualId = block.id

  useVisualInViewTracking(engagement, figureRef, visualId, 'COMPARISON')

  const wrapperClass = WIDTH_CLASS[block.width ?? 'column']
  const aspectStyle: React.CSSProperties =
    block.aspectRatio && block.aspectRatio !== 'natural'
      ? { aspectRatio: block.aspectRatio.replace(':', ' / ') }
      : {}

  const beforeLabel = block.beforeLabel ?? 'Before'
  const afterLabel  = block.afterLabel  ?? 'After'
  const pct = `${(position * 100).toFixed(2)}%`

  function updatePosition(e: React.PointerEvent) {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    setPosition(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)))
  }

  function onPointerDown(e: React.PointerEvent) {
    emitVisualInteraction(engagement, visualId, 'COMPARISON', 'DRAG')
    e.currentTarget.setPointerCapture(e.pointerId)
    setDragging(true)
    updatePosition(e)
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragging) return
    updatePosition(e)
  }

  function onPointerUp() {
    setDragging(false)
  }

  const beforeCredit = block.before.credit
  const afterCredit  = block.after.credit
  const creditLine =
    beforeCredit === afterCredit
      ? beforeCredit
      : [beforeCredit, afterCredit].filter(Boolean).join(' / ')

  return (
    <figure ref={figureRef} className={`my-0 ${wrapperClass}`}>
      {/* ── Slider container ── */}
      <div
        ref={containerRef}
        className={`relative overflow-hidden rounded select-none ${
          dragging ? 'cursor-col-resize' : 'cursor-ew-resize'
        }`}
        style={aspectStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerEnter={() => emitVisualInteraction(engagement, visualId, 'COMPARISON', 'HOVER_OVER')}
      >
        {/* Spacer — gives the container natural height when aspectRatio is 'natural' */}
        {(!block.aspectRatio || block.aspectRatio === 'natural') && (
          <img
            src={artifacts.find(a => a.id === block.after.artifactId)?.presignedUrl ?? ''}
            alt=""
            aria-hidden
            className="invisible w-full"
            draggable={false}
          />
        )}

        {/* After image — base layer, visible on right */}
        <img
          src={artifacts.find(a => a.id === block.after.artifactId)?.presignedUrl ?? ''}
          alt={block.after.altText ?? afterLabel}
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />

        {/* Before image — top layer, clipped to left portion */}
        <img
          src={artifacts.find(a => a.id === block.before.artifactId)?.presignedUrl ?? ''}
          alt={block.before.altText ?? beforeLabel}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ clipPath: `inset(0 ${(100 - position * 100).toFixed(2)}% 0 0)` }}
          draggable={false}
        />

        {/* Divider line + handle */}
        <div
          className="pointer-events-none absolute inset-y-0 z-10"
          style={{ left: pct, transform: 'translateX(-50%)' }}
        >
          {/* Line */}
          <div className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_6px_rgba(0,0,0,0.7)]" />
          {/* Handle */}
          <div className="pointer-events-auto absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 cursor-col-resize items-center justify-center rounded-full border-2 border-white bg-black/30 shadow-lg backdrop-blur-sm">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M5 4L2 8L5 12M11 4L14 8L11 12"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Before label — top-left */}
        <div className="pointer-events-none absolute left-3 top-3 z-10 flex items-center gap-1 rounded bg-black/50 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm">
          {block.before.classification && (
            <PortionMark classification={block.before.classification} disseminationControls={block.before.disseminationControls} className="text-white/80" />
          )}
          <span>{beforeLabel}</span>
        </div>

        {/* After label — top-right */}
        <div className="pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-1 rounded bg-black/50 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm">
          {block.after.classification && (
            <PortionMark classification={block.after.classification} disseminationControls={block.after.disseminationControls} className="text-white/80" />
          )}
          <span>{afterLabel}</span>
        </div>
      </div>

      {/* ── Caption ── */}
      {(block.caption || creditLine) && (
        <figcaption className="mt-1.5 space-y-0.5 text-[11px] text-fg-secondary">
          {block.caption && (
            <span className="block">
              <PortionMark classification={block.captionClassification} disseminationControls={block.captionDisseminationControls} />
              {block.caption}
            </span>
          )}
          {creditLine && (
            <span className="block italic opacity-70">{creditLine}</span>
          )}
        </figcaption>
      )}
    </figure>
  )
}
